import { useEffect, useState, type FormEvent } from 'react'
import { X } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { DEPARTMENTS, type Department } from '@/lib/departments'

/** Where a directory entry lives — mirrors the API's two writable scopes. */
export type DirectoryScope = 'artist' | 'organisation'

/** Shape returned by GET/POST `/artists/:id/email-directory[/departments]`. */
export interface DirectoryDepartment {
  id: string
  scope: DirectoryScope
  name: string
  groupEmail: string | null
  mapsToOrgDepartment: Department | null
}

const CUSTOM_VALUE = '__custom__'

export interface CreateDepartmentModalProps {
  open: boolean
  onClose: () => void
  onCreated: (result: DirectoryDepartment) => void
  defaultScope?: DirectoryScope
}

/**
 * Small centred modal for creating a department (artist-scope custom
 * department, or an organisation-scope fixed-department group email) from
 * inside the email routing flow designer. Standalone — Task 6 wires it into
 * DirectoryPanel / RuleEditorSheet via `open` + `onCreated`.
 */
export function CreateDepartmentModal({
  open,
  onClose,
  onCreated,
  defaultScope = 'artist',
}: CreateDepartmentModalProps) {
  const [scope, setScope] = useState<DirectoryScope>(defaultScope)
  const [selection, setSelection] = useState<string>('')
  const [customName, setCustomName] = useState('')
  const [groupEmail, setGroupEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setScope(defaultScope)
    setSelection('')
    setCustomName('')
    setGroupEmail('')
    setError(null)
    setSubmitting(false)
  }, [open, defaultScope])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      // Capture phase + stopImmediatePropagation so this doesn't also bubble
      // up and close a parent RuleEditorSheet/DirectoryPanel this modal is
      // stacked on top of.
      e.stopImmediatePropagation()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onClose])

  if (!open) return null

  const isCustom = selection === CUSTOM_VALUE
  const selectedDept = DEPARTMENTS.find((d) => d.value === selection)
  const name = isCustom ? customName.trim() : (selectedDept?.label ?? '')
  const mapsToOrgDepartment = isCustom ? undefined : (selectedDept?.value as Department | undefined)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)

    if (!name) {
      setError('Choose a department, or pick Custom and give it a name')
      return
    }
    if (scope === 'organisation' && !mapsToOrgDepartment) {
      setError('Organisation-level departments must be one of the fixed departments')
      return
    }

    setSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        scope,
        name,
        groupEmail: groupEmail.trim() || undefined,
        mapsToOrgDepartment,
      }
      const result = await artistFetch<DirectoryDepartment>('/email-directory/departments', {
        method: 'POST',
        body,
      })
      onCreated(result)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Failed to create department')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-ovation-ink/30 backdrop-blur-[2px]"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative flex w-full max-w-sm flex-col rounded-xl border border-ovation-ink/10 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-ovation-ink/10 px-5 py-4">
          <h2 className="text-base font-semibold text-ovation-ink">New department</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-ovation-muted hover:bg-ovation-ink/5 hover:text-ovation-ink"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col">
          <div className="space-y-4 px-5 py-4">
            <fieldset className="flex gap-2">
              <legend className="mb-1 block text-xs font-medium uppercase tracking-wider text-ovation-muted">
                Scope
              </legend>
              {(
                [
                  ['artist', 'This artist'],
                  ['organisation', 'Whole organisation'],
                ] as const
              ).map(([value, label]) => (
                <label
                  key={value}
                  className={
                    'flex flex-1 cursor-pointer items-center justify-center rounded-md border px-2 py-1.5 text-xs transition-colors ' +
                    (scope === value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-800'
                      : 'border-ovation-ink/10 hover:bg-ovation-ink/5')
                  }
                >
                  <input
                    type="radio"
                    name="dept-scope"
                    value={value}
                    checked={scope === value}
                    onChange={() => {
                      setScope(value)
                      if (value === 'organisation' && isCustom) setSelection('')
                    }}
                    className="sr-only"
                  />
                  {label}
                </label>
              ))}
            </fieldset>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ovation-muted">
                Department
              </label>
              <select
                required
                value={selection}
                onChange={(e) => setSelection(e.target.value)}
                className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
              >
                <option value="" disabled>
                  Select a department…
                </option>
                {DEPARTMENTS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
                {scope === 'artist' && <option value={CUSTOM_VALUE}>Custom…</option>}
              </select>
            </div>

            {isCustom && (
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ovation-muted">
                  Custom name
                </label>
                <input
                  required
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="e.g. Wardrobe"
                  className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
                />
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-ovation-muted">
                Group email (optional)
              </label>
              <input
                type="email"
                value={groupEmail}
                onChange={(e) => setGroupEmail(e.target.value)}
                placeholder="legal@example.com"
                className="w-full rounded-lg border border-ovation-ink/15 px-3 py-2 text-sm"
              />
              <p className="mt-1 text-xs text-ovation-muted">
                Used as the routing target when no head is set.
              </p>
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
          </div>

          <div className="flex gap-2 border-t border-ovation-ink/10 px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-ovation-ink/15 px-4 py-2 text-sm font-medium hover:bg-ovation-ink/5"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-lg bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              {submitting ? 'Creating…' : 'Create department'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
